/** ************************************************************************
 * utils/exportToXLIFFFlat.js   – 2025-06-07
 *
 *  • flat <group> per CSV row
 *  • translatable fields: userTitle, paragraph, calloutText,
 *    definition, legalParagraph, youtubeVideo
 *  • non-translatable admin/media fields translate="no"
 *  • <note exportTs>             (dest-language timestamp)
 *    <note contentDateUpdatedSrc> (site-1 last edit)
 *  • debug line for rows with no fields
 ************************************************************************* */
import he from "he";
import { buildExportStructure } from "./buildExportCsv";
import { resolveManual } from "../craft/dataResolvers";
import { canonCamelCase, applyVariables } from ".";

/* ------------------------------------------------------------------ */
const TRANSLATABLE = new Set([
  "userTitle",
  "paragraph",
  "calloutText",
  "definition",
  "legalParagraph",
  "youtubeVideo",
  "richText",
]);

const LABELS = {
  userTitle: "User Title",
  paragraph: "Paragraph",
  calloutText: "Call-out",
  definition: "Definition",
  legalParagraph: "Legal",
  youtubeVideo: "YouTube URL",
  title: "Title (admin)",
  RichText: "Rich Text",
  imageUrl: "Image URL",
};

const escapeCdata = (v = "") =>
  `<![CDATA[${String(v).replace(/]]>/g, "]]]]><![CDATA[>")}]]>`;
// ── attributes may not contain entities *or* CDATA, so:
//    ① convert every space to '-' ② URI-encode the result
const encodeAttr = (v = "") =>
  he.encode(String(v), { useNamedReferences: true });
const DBG = (...m) => console.log("[XLIFF-DBG]", ...m);

/* ------------------------------------------------------------------ */
/*  flatten manual → Map                                               */
/* ------------------------------------------------------------------ */
function flattenToMap(manual, isSrc = false, parseVars = true) {
  const map = new Map();

  /* grab variable map once so we can expand strings quickly */
  const varMap = parseVars ? resolveManual(manual).variableValues || {} : null;

  /* helper */
  const V = str => (parseVars ? applyVariables(str, varMap) : str);

  buildExportStructure(manual, true).forEach(r => {
    const key = `${r.entryId}|${canonCamelCase(r.entryType)}`;

    if (!map.has(key)) {
      map.set(key, {
        type: canonCamelCase(r.entryType),
        title: V(r.entryUserTitle),
        fields: {},
        meta: {
          exportTs: r.exportTimestamp || "",
          contentUpdatedSrc: isSrc
            ? r.contentDateUpdated || r.dateUpdated || ""
            : "",
        },
      });
    }

    const bag = map.get(key).fields;

    if (r.entryUserTitle !== undefined) bag.userTitle = V(r.entryUserTitle);

    /* entryContent is paragraph  OR  calloutText */
    if (r.entryContent !== undefined) {
      if (r.entryType === "calloutBlock") bag.calloutText = V(r.entryContent);
      else bag.paragraph = V(r.entryContent);
    }

    /* direct cols */
    if (r.paragraph !== undefined) bag.paragraph = V(r.paragraph);
    if (r.calloutText !== undefined) bag.calloutText = V(r.calloutText);
    if (r.youtubeVideo !== undefined) bag.youtubeVideo = V(r.youtubeVideo);
    if (r.definition !== undefined) bag.definition = V(r.definition);
    if (r.legalParagraph !== undefined)
      bag.legalParagraph = V(r.legalParagraph);
  });

  /* warn on empty bags */
  map.forEach((rec, key) => {
    if (Object.keys(rec.fields).length === 0) {
      const [id, type] = key.split("|");
      if (
        type === "articleLongformComposableEntries" ||
        type === "topicArticleStepBuilderBlock" ||
        [
          "textBlock",
          "textPlusImageBlock",
          "calloutBlock",
          "videoBlock",
          "columnComposableBlock",
          "richTextInlineImageBlock",
        ].includes(type)
      ) {
        DBG(
          `NO FIELDS → id=${id}, type=${type}, title="${rec.title || ""}"` +
            `, exportTs=${rec.meta.exportTs || "—"}`
        );
      }
    }
  });

  return map;
}

/* ------------------------------------------------------------------ */
/*  main exporter                                                      */
/* ------------------------------------------------------------------ */
export function exportToXLIFF(srcRaw, trgRaw) {
  /* 1️⃣ for filenames / lang we still like resolveManual … */
  const srcResolved = resolveManual(srcRaw);
  const trgResolved = resolveManual(trgRaw);

  /* 2️⃣ but for rows we need the RAW trees */
  const srcFlat = flattenToMap(srcRaw, true, true);
  const trgFlat = flattenToMap(trgRaw, false, true);

  const allKeys = new Set([...srcFlat.keys(), ...trgFlat.keys()]);
  let body = "";

  allKeys.forEach(k => {
    const [id, type] = k.split("|");
    const srcRec = srcFlat.get(k) || {};
    const trgRec = trgFlat.get(k) || {};
    const srcFields = srcRec.fields || {};
    const trgFields = trgRec.fields || {};
    const titleRes = srcRec.title || srcFields.userTitle || type;

    body +=
      `    <group id="${k}" resname="${encodeAttr(titleRes)}"` +
      ` restype="${encodeAttr(type)}">\n`;

    if (trgRec.meta?.exportTs)
      body +=
        `        <note from="exportTs" annotates="entry">` +
        `${trgRec.meta.exportTs}</note>\n`;
    if (srcRec.meta?.contentUpdatedSrc)
      body +=
        `        <note from="contentDateUpdatedSrc" annotates="entry">` +
        `${srcRec.meta.contentUpdatedSrc}</note>\n`;

    const fields = new Set([
      ...Object.keys(srcFields),
      ...Object.keys(trgFields),
    ]);

    fields.forEach(f => {
      const sVal = srcFields[f] ?? "";
      const tVal = trgFields[f] ?? "";
      if (!sVal && !tVal) return;

      const trFlag = TRANSLATABLE.has(f) ? "" : ' translate="no"';

      body +=
        `        <trans-unit id="${id}-${f}" resname="${f}"${trFlag}>\n` +
        `            <source>${escapeCdata(sVal)}</source>\n` +
        `            <target>${escapeCdata(tVal)}</target>\n` +
        `            <note>${LABELS[f] || f}</note>\n` +
        `        </trans-unit>\n`;
    });

    body += "    </group>\n";
  });

  return `<?xml version="1.0" encoding="UTF-8"?>
<xliff version="1.2">
  <file original="manual:${srcResolved.id}"
        datatype="plaintext"
        source-language="en"
        target-language="${trgResolved.language || "xx"}">
${body}  </file>
</xliff>`;
}

export default exportToXLIFF;
